import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Input, Space, Popconfirm, Tag } from 'antd';
import { EditOutlined, SearchOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { WordPressAPI } from '../../services/api/api';
import { WCPalletLayout, WCPallet } from '../../types/wordpress';
import PalletLayoutForm from './PalletLayoutForm';
import { decodeHtmlEntities } from '../../utils/helpers';

const PalletLayoutList: React.FC = () => {
    const [layouts, setLayouts] = useState<WCPalletLayout[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingLayout, setEditingLayout] = useState<WCPalletLayout | undefined>(undefined);
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pallets, setPallets] = useState<WCPallet[]>([]);

    useEffect(() => {
        fetchLayouts();
        fetchPallets();
    }, [currentPage, pageSize, searchText]);

    const fetchLayouts = async () => {
        setLoading(true);
        try {
            const response = await WordPressAPI.getPalletLayouts({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });
            setLayouts(response.items);
            setTotal(response.total);
        } catch (error) {
            message.error('Failed to fetch layouts');
        } finally {
            setLoading(false);
        }
    };

    const fetchPallets = async () => {
        try {
            const response = await WordPressAPI.getPallets({});
            setPallets(response.items);
        } catch (error) {
            message.error('Failed to fetch pallets');
        }
    };

    const handleEdit = (layout: WCPalletLayout) => {
        setEditingLayout(layout);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingLayout(undefined);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingLayout(undefined);
    };

    const handleFormSubmit = async (values: Partial<WCPalletLayout>) => {
        try {
            setLoading(true);
            if (editingLayout?.id) {
                await WordPressAPI.updatePalletLayout(editingLayout.id, values);
                message.success('Layout updated successfully');
            } else {
                await WordPressAPI.createPalletLayout(values);
                message.success('Layout created successfully');
            }
            handleModalClose();
            await fetchLayouts();
        } catch (error) {
            message.error('Failed to save layout');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async (layout: WCPalletLayout) => {
        try {
            setLoading(true);
            await WordPressAPI.updatePalletLayout(layout.id, { is_deleted: !layout.is_deleted }
            );
            message.success(`Layout ${layout.is_deleted ? 'activated' : 'deactivated'} successfully`);
            await fetchLayouts();
        } catch (error) {
            message.error('Failed to update layout status');
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnsType<WCPalletLayout> = [
        {
            title: 'Name',
            dataIndex: 'title',
            key: 'title',
            sorter: true,
            render: (text: any) => decodeHtmlEntities(text.rendered)
        },
        {
            title: 'Pallet',
            key: 'pallet',
            render: (_, record) => {
                const pallet = pallets.find(p => p.id === record.meta.pallet_id);
                return pallet ? decodeHtmlEntities(pallet.title.rendered) : 'N/A';
            },
        },
        {
            title: 'Products',
            key: 'products',
            render: (_, record) => record.meta.products?.length || 0,
        },
        {
            title: 'Fill %',
            key: 'products',
            render: (_, record) => (record.meta.products?.reduce((acc, product) => acc + product.percentage, 0) || 0) + '%',
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                if (value === 'active') {
                    return !record.is_deleted;
                }
                return record.is_deleted;
            },
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Inactive</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="flex flex-col sm:flex-row gap-2">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="whitespace-nowrap"
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this Layout?"
                            onConfirm={() => handleDeactivate(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="whitespace-nowrap"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this layout?"
                            onConfirm={() => handleDeactivate(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="whitespace-nowrap"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">Pallet Layouts</h2>
                <Space>
                    <Input
                        placeholder="Search layouts..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-64"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                    >
                        Add New Layout
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={layouts}
                loading={loading}
                rowKey="id"
                rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                pagination={{
                    total: total,
                    current: currentPage,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
            />

            <Modal
                title={editingLayout ? 'Edit Layout' : 'Add New Layout'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                destroyOnClose
            >
                <PalletLayoutForm
                    key={editingLayout?.id || 'new'}
                    layout={editingLayout}
                    onSave={handleFormSubmit}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default PalletLayoutList; 